import Link from "next/link";
import { resolvePayloadUrl, getLinkAttributes, type PageParam } from "@/lib/url-utils";

/**
 * PayloadLink Component
 *
 * Universal link component for Payload CMS data.
 * Handles all link types (page, custom, phone, email) with proper
 * attributes and behavior. This replaces duplicate link logic
 * across NavItem, CTAButtons, and other components.
 */

export interface PayloadLinkProps {
  /** Link type from Payload */
  type?: "page" | "custom" | "phone" | "email" | "dropdown";
  /** Page object, polymorphic relationship, or ID for page type links */
  page?: PageParam;
  /** Custom URL for custom type links */
  url?: string | null;
  /** Phone number for phone type links */
  phone?: string | null;
  /** Email address for email type links */
  email?: string | null;
  /** Force open in new tab */
  newTab?: boolean | null;
  /** Enable prefetching (default: true for faster navigation) */
  prefetch?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Click handler */
  onClick?: () => void;
  /** Children to render inside the link */
  children: React.ReactNode;
  /** Any additional props to pass to the Link component */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export function PayloadLink({
  type,
  page,
  url,
  phone,
  email,
  newTab,
  prefetch = true,
  className,
  onClick,
  children,
  ...rest
}: PayloadLinkProps) {
  // Resolve the URL based on the link type
  const href = resolvePayloadUrl(type, { page, url, phone, email });
  
  // Get appropriate link attributes (target, rel)
  const linkAttributes = getLinkAttributes(href, newTab ?? undefined);
  
  // For dropdown type or invalid hrefs, render a button instead
  if (type === "dropdown" || href === "#") {
    return (
      <button
        className={className}
        onClick={onClick}
        type="button"
        {...rest}
      >
        {children}
      </button>
    );
  }
  
  // For phone and email links, use a regular anchor tag
  // (Next.js Link doesn't handle these well)
  if (href.startsWith("tel:") || href.startsWith("mailto:")) {
    return (
      <a
        href={href}
        className={className}
        onClick={onClick}
        {...linkAttributes}
        {...rest}
      >
        {children}
      </a>
    );
  }
  
  // For all other links, use Next.js Link for client-side navigation
  // prefetch=true enables automatic prefetching when link enters viewport
  return (
    <Link
      href={href}
      prefetch={prefetch}
      className={className}
      onClick={onClick}
      {...linkAttributes}
      {...rest}
    >
      {children}
    </Link>
  );
}

/**
 * Simplified version for when you already have a resolved URL
 */
export interface SimpleLinkProps {
  href: string;
  newTab?: boolean;
  prefetch?: boolean;
  className?: string;
  onClick?: () => void;
  children: React.ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export function SimplePayloadLink({
  href,
  newTab,
  prefetch = true,
  className,
  onClick,
  children,
  ...rest
}: SimpleLinkProps) {
  const linkAttributes = getLinkAttributes(href, newTab);
  
  // For phone and email links
  if (href.startsWith("tel:") || href.startsWith("mailto:")) {
    return (
      <a
        href={href}
        className={className}
        onClick={onClick}
        {...linkAttributes}
        {...rest}
      >
        {children}
      </a>
    );
  }
  
  // For regular links
  return (
    <Link
      href={href}
      prefetch={prefetch}
      className={className}
      onClick={onClick}
      {...linkAttributes}
      {...rest}
    >
      {children}
    </Link>
  );
}